<template>
  <v-dialog
    max-width="600"
    :value="value"
    :persistent="loading"
    :no-click-animation="loading"
    @input="$emit('input:dialog', $event)"
  >
    <v-card>
      <v-card-title class="headline">
        {{ $t('message.searcher.savedSearch.editSearches') }}
      </v-card-title>
      <v-card-text>
        <v-list dense>
          <template v-for="(searches, group) in groupedSearches">
            <v-subheader :key="group">{{ $tc(`message.searcher.tab.${group}`) }}</v-subheader>
            <template v-for="search in searches">
              <v-list-tile :key="`search-${search.uuid}`" ripple @click="editingSearch = search">
                <v-list-tile-content>
                  <v-list-tile-title
                    v-if="search !== editingSearch"
                    v-text="search.name"
                  />
                  <v-container fluid v-else pl-0 pr-0>
                    <v-text-field
                      @blur="editingSearch = null"
                      autofocus
                      single-line
                      clearable
                      ref="searchInput"
                      required
                      v-model="search.name"
                      :error="!search.name"
                      @keypress.enter="editingSearch = null"
                    />
                  </v-container>
                </v-list-tile-content>

                <v-list-tile-action v-if="search !== editingSearch">
                  <v-btn icon outlined>
                    <v-icon color="grey darken-1">edit</v-icon>
                  </v-btn>
                </v-list-tile-action>

                <v-list-tile-action @click="handleDelete(search)">
                  <v-btn icon outlined>
                    <v-icon color="grey darken-1">delete</v-icon>
                  </v-btn>
                </v-list-tile-action>

              </v-list-tile>
            </template>
          </template>

          <v-list-tile v-if="!searches.length && !loading">
            <v-list-tile-content class="d-block text-xs-center nx-text--level-1">
              {{ $t('message.searcher.savedSearch.empty') }}
            </v-list-tile-content>
          </v-list-tile>

          <v-list-tile v-else-if="!draftSearches.length && !loading">
            <v-list-tile-content class="d-block text-xs-center nx-text--level-1">
              {{ $t('message.searcher.savedSearch.allSearchesDeleted') }}
            </v-list-tile-content>
          </v-list-tile>

        </v-list>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="secondary"
          flat
          :disabled="loading"
          @click="$emit('input:dialog', false)"
        >{{ $t('message.searcher.savedSearch.cancel') }}
        </v-btn>
        <v-btn color="secondary" flat :loading="loading" :disabled="hasErrors" @click="updateSearches">
          {{ $t('message.searcher.savedSearch.done') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { cloneDeep, pick } from 'lodash';
import { mapState } from 'vuex';
import { toArray } from '@/utils';

export default {
  name: 'nx-saved-searches-manage-dialog',
  props: {
    value: Boolean,
  },
  data() {
    return {
      draftSearches: [],
      loading: false,
      editingSearch: null,
    };
  },
  computed: {
    ...mapState({
      searches: state => state.searcher.savedSearches,
    }),
    groupedSearches() {
      const groupedSearches = this.draftSearches.reduce((result, search) => {
        const group = search.searcher;
        result[group] = result[group] || [];
        result[group].push(search);
        return result;
      }, {});

      if (this.showOnlySearchesForEntity) {
        return pick(groupedSearches, this.showOnlySearchesForEntity);
      }

      return groupedSearches;
    },
    hasErrors() {
      return toArray(this.draftSearches)
        .some(search => !search.name.trim());
    },
  },
  methods: {
    handleDelete(searchToDelete) {
      this.draftSearches = this.draftSearches.filter(search => search.uuid !== searchToDelete.uuid);
    },
    async updateSearches() {
      this.loading = true;

      // array que contiene aquellas búsquedas que han editado su nombre
      const diffSearches = this.draftSearches.filter((search, index) => this.searches[index].name !== search.name);
      const updates = diffSearches.map(search => this.$store.dispatch('searcher/EDIT_SAVED_SEARCH', search));
      const removes = this.searches
        .filter(search => !this.draftSearches.find(item => item.uuid === search.uuid))
        .map(search => this.$store.dispatch('searcher/REMOVE_SAVED_SEARCH', search.uuid));
      await Promise.all([...updates, ...removes]);

      this.loading = false;
      this.dismiss();
    },
    dismiss() {
      this.$emit('input:dialog', false);
    },
  },
  watch: {
    value: {
      handler() {
        if (this.value) {
          this.draftSearches = cloneDeep(this.searches);
        }
      },
      immediate: true,
    },
  },
};
</script>
